import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FaChartLine } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import { useAuth } from "../../../context/AuthContext";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  convertLegacyRole,
  convertLegacyRoleWithContext,
  isBUAdmin,
  isCompanyAdmin,
  isManagerRole,
  isRecruiterRole,
  isSalesRole
} from "../../../constants/roles";
import { getMyPerformance, getTeamPerformance } from "../../../api/performanceApi";
import {
  PageHeader,
  Panel,
  KpiStrip,
  KpiCard,
  EmptyState,
} from "../../../components/Portal/ui";

// Corporate Azure palette (Recharts can't read Chakra tokens — inline hex).
const CHART_NAVY = "#153B69";
const CHART_NAVY_3 = "#1E4D78";
const CHART_MINT = "#1BAE9A";
const CHART_SELECTED = "#1BAE9A";
const CHART_LINE = "#1E4D78";
const CHART_GRID = "#D6E0EA";
const CHART_AXIS = "#617386";

// Compact filter Select matching the template's 31px controls.
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
};

const filterLabelProps = {
  textTransform: "uppercase",
  fontSize: "10px",
  fontWeight: "800",
  letterSpacing: "0.055em",
  color: "portal.muted",
  sx: { marginBottom: "4px !important" },
};

const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const bucketLabel = (b, idx) => {
  if (!b || typeof b !== "object") return `#${idx + 1}`;
  return (
    b.label ||
    b.key ||
    b.bucket ||
    b.period ||
    b.periodLabel ||
    b.yearWeek ||
    b.year_week ||
    b.yearMonth ||
    b.year_month ||
    (b.year && b.month ? `${b.year}-${String(b.month).padStart(2, "0")}` : null) ||
    (b.year && b.week ? `${b.year}-W${String(b.week).padStart(2, "0")}` : null) ||
    b.year ||
    `#${idx + 1}`
  );
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 6);
  const toStr = to.toISOString().slice(0, 10);
  const fromStr = from.toISOString().slice(0, 10);
  return { fromDate: fromStr, toDate: toStr };
};

const normalizeRate = (value) => {
  const v = safeNumber(value);
  if (!Number.isFinite(v)) return 0;
  return v > 1 ? v / 100 : v;
};

const resolveConversionRate = ({
  directValue,
  numerator,
  denominator,
  numeratorFallback,
  denominatorFallback
}) => {
  if (Number.isFinite(Number(directValue))) {
    return normalizeRate(directValue);
  }
  const num = safeNumber(numerator);
  const den = safeNumber(denominator);
  if (den > 0) return num / den;
  if (denominatorFallback > 0) return numeratorFallback / denominatorFallback;
  return 0;
};

const getOpportunitiesTotal = (b, offMarket, referral) => {
  const total = safeNumber(b?.opportunitiesTotal);
  return total || offMarket + referral;
};

const buildSalesMetrics = (b) => {
  const offMarketOpportunities = safeNumber(b?.offMarketOpportunities);
  const referralOpportunities = safeNumber(b?.referralOpportunities);
  const opportunitiesTotal = getOpportunitiesTotal(b, offMarketOpportunities, referralOpportunities);
  const recruiterProfilesReceived = safeNumber(b?.recruiterProfilesReceived);
  const avgAsapSkillsScoreProposed = safeNumber(b?.avgAsapSkillsScoreProposed);
  const candidatesSubmitted = safeNumber(b?.candidatesSubmitted);
  const clientInterviews = safeNumber(b?.clientInterviews);
  const wins = safeNumber(b?.wins);
  const conversionOppToInterview = resolveConversionRate({
    directValue: b?.conversionOppToInterview,
    numerator: b?.oppToInterviewNumerator,
    denominator: b?.oppToInterviewDenominator,
    numeratorFallback: clientInterviews,
    denominatorFallback: opportunitiesTotal
  });
  const conversionOppToWin = resolveConversionRate({
    directValue: b?.conversionOppToWin,
    numerator: b?.oppToWinNumerator,
    denominator: b?.oppToWinDenominator,
    numeratorFallback: wins,
    denominatorFallback: opportunitiesTotal
  });
  return {
    offMarketOpportunities,
    referralOpportunities,
    opportunitiesTotal,
    recruiterProfilesReceived,
    avgAsapSkillsScoreProposed,
    candidatesSubmitted,
    clientInterviews,
    wins,
    conversionOppToInterview,
    conversionOppToWin
  };
};

const buildRecruiterMetrics = (b) => {
  const staffingNeedsToCover = safeNumber(b?.staffingNeedsToCover);
  const profilesProposed = safeNumber(b?.profilesProposed);
  const avgAtsScore = safeNumber(b?.avgAtsScore);
  const avgAsapSkillsScoreProposed = safeNumber(b?.avgAsapSkillsScoreProposed);
  const leadTimeOppToFirstProfile = safeNumber(b?.leadTimeOppToFirstProfile);
  return {
    staffingNeedsToCover,
    profilesProposed,
    avgAtsScore,
    avgAsapSkillsScoreProposed,
    leadTimeOppToFirstProfile
  };
};

const buildBucket = (b, idx) => ({
  label: bucketLabel(b, idx),
  ...buildSalesMetrics(b),
  ...buildRecruiterMetrics(b)
});

const formatNumber = (value, decimals = 0) => {
  if (!Number.isFinite(Number(value))) return "-";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  }).format(Number(value));
};

const formatPercent = (value) => `${(normalizeRate(value) * 100).toFixed(1)}%`;

// Recharts tooltip styled to the navy/line tokens.
const rechartsTooltipStyle = {
  fontSize: "12px",
  borderRadius: "10px",
  border: `1px solid ${CHART_GRID}`,
  boxShadow: "0 10px 24px rgba(16,32,51,.08)",
};

const MetricCard = ({ title, children }) => (
  <Box
    bg="portal.panel"
    borderRadius="10px"
    borderWidth="1px"
    borderColor="portal.line"
    boxShadow="kpi"
    p="12px"
  >
    <Text
      textTransform="uppercase"
      fontSize="10.5px"
      fontWeight="800"
      letterSpacing="0.055em"
      color="portal.muted"
      sx={{ marginBottom: "8px !important" }}
    >
      {title}
    </Text>
    {children}
  </Box>
);

const MetricBarChart = ({ data, dataKey, color, selectedIndex, onSelect, valueFormatter }) => (
  <ResponsiveContainer width="100%" height={180}>
    <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <YAxis tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <RechartsTooltip formatter={(value) => valueFormatter(value)} contentStyle={rechartsTooltipStyle} />
      <Bar
        dataKey={dataKey}
        fill={color}
        radius={[4, 4, 0, 0]}
        onClick={(_, index) => {
          if (typeof onSelect === "function") onSelect(index);
        }}
      >
        {data.map((_, idx) => (
          <Cell key={`cell-${dataKey}-${idx}`} fill={idx === selectedIndex ? CHART_SELECTED : color} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const MetricLineChart = ({
  data,
  dataKey,
  color,
  selectedIndex,
  onSelect,
  valueFormatter,
  yAxisFormatter
}) => (
  <ResponsiveContainer width="100%" height={180}>
    <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_AXIS }} stroke={CHART_GRID} />
      <YAxis tick={{ fontSize: 11, fill: CHART_AXIS }} tickFormatter={yAxisFormatter} stroke={CHART_GRID} />
      <RechartsTooltip formatter={(value) => valueFormatter(value)} contentStyle={rechartsTooltipStyle} />
      <Legend wrapperStyle={{ fontSize: "11px" }} />
      <Line
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3, fill: color }}
        activeDot={{ r: 5, fill: CHART_SELECTED }}
        onClick={(_, index) => {
          if (typeof onSelect === "function") onSelect(index);
        }}
      />
    </LineChart>
  </ResponsiveContainer>
);

const PerformanceDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("portal");
  const { user } = useAuth();
  const { businessUnits, currentRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } =
    useBusinessUnit();

  const [bucket, setBucket] = useState("month");
  const [range, setRange] = useState(() => defaultRange());
  const [roleScope, setRoleScope] = useState("all");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resolvedName, setResolvedName] = useState(null);
  const [resolvedRole, setResolvedRole] = useState(null);

  const userRole = useMemo(() => {
    const hasBUs = Boolean(businessUnits && businessUnits.length > 0);
    return convertLegacyRoleWithContext(user?.role, hasBUs);
  }, [businessUnits, user?.role]);
  const buRole = useMemo(() => convertLegacyRole(currentRole), [currentRole]);
  const canSeeTeam = useMemo(
    () => isCompanyAdmin(userRole) || isBUAdmin(buRole) || isManagerRole(buRole),
    [buRole, userRole]
  );

  const fetchData = useCallback(async () => {
    if (!isBUContextLoaded) return;
    setLoading(true);
    try {
      const baseParams = {
        bucket,
        fromDate: range.fromDate || undefined,
        toDate: range.toDate || undefined
      };

      if (!canSeeTeam) {
        const me = await getMyPerformance(baseParams, { buId: selectedBusinessUnitId || undefined });
        setResult(me || null);
        setResolvedName(t("pilotPerformance.detail.meTitle"));
        setResolvedRole(buRole);
        return;
      }

      const params = {
        ...baseParams,
        roleScope: roleScope || "all",
        userIds: userId ? String(userId) : undefined
      };
      const team = await getTeamPerformance(params, { buId: selectedBusinessUnitId || undefined });
      const users = Array.isArray(team?.users) ? team.users : [];
      const found =
        users.find((u) => String(u?.id ?? u?.userId ?? u?.user_id ?? u?.user?.id) === String(userId)) ||
        users[0] ||
        null;

      const name =
        `${String(found?.firstName ?? found?.first_name ?? found?.user?.firstName ?? found?.user?.first_name ?? "").trim()} ${String(found?.lastName ?? found?.last_name ?? found?.user?.lastName ?? found?.user?.last_name ?? "").trim()}`.trim() ||
        found?.name ||
        found?.fullName ||
        found?.full_name ||
        found?.user?.name ||
        found?.user?.fullName ||
        found?.user?.full_name ||
        found?.email ||
        found?.user?.email ||
        null;

      const buckets = Array.isArray(found?.buckets)
        ? found.buckets
        : Array.isArray(found?.series)
        ? found.series
        : [];

      setResolvedName(name || t("pilotPerformance.detail.titleFallback"));
      setResolvedRole(convertLegacyRole(found?.role) || null);
      setResult({ buckets });
    } catch {
      setResolvedName(null);
      setResolvedRole(null);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [
    bucket,
    canSeeTeam,
    isBUContextLoaded,
    range.fromDate,
    range.toDate,
    roleScope,
    selectedBusinessUnitId,
    t,
    userId,
    buRole
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buckets = useMemo(() => (Array.isArray(result?.buckets) ? result.buckets : []), [result]);
  const chartSeries = useMemo(() => (buckets || []).map(buildBucket), [buckets]);

  const [selectedBucketIndex, setSelectedBucketIndex] = useState(0);

  useEffect(() => {
    if (!chartSeries || chartSeries.length === 0) {
      setSelectedBucketIndex(0);
      return;
    }
    setSelectedBucketIndex((idx) => Math.min(Math.max(0, idx), chartSeries.length - 1));
  }, [chartSeries]);

  const selectedBucket = chartSeries[selectedBucketIndex] || null;

  const roleScopeLabel = (v) => {
    if (v === "sales") return t("pilotPerformance.list.filters.roleScopeSales");
    if (v === "recruiter") return t("pilotPerformance.list.filters.roleScopeRecruiter");
    return t("pilotPerformance.list.filters.roleScopeAll");
  };

  const activityRole = useMemo(() => {
    if (isSalesRole(resolvedRole)) return "sales";
    if (isRecruiterRole(resolvedRole)) return "recruiter";
    return "sales";
  }, [resolvedRole]);

  const showSalesSection = activityRole === "sales";
  const showRecruiterSection = activityRole === "recruiter";

  const salesMetrics = useMemo(
    () => [
      {
        key: "offMarketOpportunities",
        label: t("pilotPerformance.metrics.offMarketOpportunities"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "referralOpportunities",
        label: t("pilotPerformance.metrics.referralOpportunities"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "recruiterProfilesReceived",
        label: t("pilotPerformance.metrics.recruiterProfilesReceived"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "avgAsapSkillsScoreProposed",
        label: t("pilotPerformance.metrics.avgAsapSkillsScoreProposed"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "candidatesSubmitted",
        label: t("pilotPerformance.metrics.candidatesSubmitted"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "clientInterviews",
        label: t("pilotPerformance.metrics.clientInterviews"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "wins",
        label: t("pilotPerformance.metrics.wins"),
        color: CHART_MINT,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "conversionOppToInterview",
        label: t("pilotPerformance.metrics.conversionOppToInterview"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatPercent(v)
      },
      {
        key: "conversionOppToWin",
        label: t("pilotPerformance.metrics.conversionOppToWin"),
        color: CHART_NAVY,
        chart: "line",
        format: (v) => formatPercent(v)
      }
    ],
    [t]
  );

  const recruiterMetrics = useMemo(
    () => [
      {
        key: "staffingNeedsToCover",
        label: t("pilotPerformance.metrics.staffingNeedsToCover"),
        color: CHART_NAVY,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "profilesProposed",
        label: t("pilotPerformance.metrics.profilesProposed"),
        color: CHART_NAVY_3,
        chart: "bar",
        format: (v) => formatNumber(v, 0)
      },
      {
        key: "avgAtsScore",
        label: t("pilotPerformance.metrics.avgAtsScore"),
        color: CHART_LINE,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "avgAsapSkillsScoreProposed",
        label: t("pilotPerformance.metrics.avgAsapSkillsScoreProposed"),
        color: CHART_MINT,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      },
      {
        key: "leadTimeOppToFirstProfile",
        label: t("pilotPerformance.metrics.leadTimeOppToFirstProfile"),
        color: CHART_NAVY_3,
        chart: "line",
        format: (v) => formatNumber(v, 1)
      }
    ],
    [t]
  );

  const renderMetricChart = (metric) => {
    if (metric.chart === "line") {
      const isPercent = metric.key.includes("conversion");
      return (
        <MetricLineChart
          data={chartSeries}
          dataKey={metric.key}
          color={metric.color}
          selectedIndex={selectedBucketIndex}
          onSelect={setSelectedBucketIndex}
          valueFormatter={metric.format}
          yAxisFormatter={isPercent ? (v) => `${(normalizeRate(v) * 100).toFixed(0)}%` : undefined}
        />
      );
    }
    return (
      <MetricBarChart
        data={chartSeries}
        dataKey={metric.key}
        color={metric.color}
        selectedIndex={selectedBucketIndex}
        onSelect={setSelectedBucketIndex}
        valueFormatter={metric.format}
      />
    );
  };

  const renderMetricSummary = (metrics) => (
    <KpiStrip columns={{ base: 2, md: 3, xl: 4 }}>
      {metrics.map((metric) => (
        <KpiCard
          key={metric.key}
          label={metric.label}
          value={metric.format(selectedBucket?.[metric.key])}
        />
      ))}
    </KpiStrip>
  );

  const summaryLabel = loading
    ? t("pilotPerformance.detail.summary.loading")
    : t("pilotPerformance.detail.summary.range", {
        start: range.fromDate || "-",
        end: range.toDate || "-"
      });

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="PE"
        eyebrow={t("pilotPerformance.list.eyebrow", "ASAP PILOT")}
        title={resolvedName || t("pilotPerformance.detail.titleFallback")}
        actions={
          <Button
            variant="secondary"
            size="sm"
            h="32px"
            leftIcon={<ArrowBackIcon boxSize="11px" />}
            onClick={() => navigate("/portal/asap-pilot/performance")}
            aria-label={t("pilotPerformance.detail.backAria")}
          >
            {t("pilotPerformance.detail.backToList", "Back")}
          </Button>
        }
      />

      <Box p="20px">
        {/* Filters */}
        <Panel mb="12px">
          <Box px="12px" py="12px">
            <Flex gap="12px" wrap="wrap" align="flex-end">
              <Box>
                <Text {...filterLabelProps}>{t("pilotPerformance.list.filters.bucket")}</Text>
                <Select
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  w="160px"
                  sx={filterSelectSx}
                >
                  <option value="week">{t("pilotPerformance.list.filters.bucketWeek")}</option>
                  <option value="month">{t("pilotPerformance.list.filters.bucketMonth")}</option>
                  <option value="year">{t("pilotPerformance.list.filters.bucketYear")}</option>
                </Select>
              </Box>

              <Box>
                <Text {...filterLabelProps}>{t("pilotPerformance.list.filters.fromDate")}</Text>
                <Input
                  type="date"
                  value={range.fromDate}
                  onChange={(e) => setRange((r) => ({ ...r, fromDate: e.target.value }))}
                  h="31px"
                  w="160px"
                  fontSize="12px"
                  fontWeight="600"
                />
              </Box>

              <Box>
                <Text {...filterLabelProps}>{t("pilotPerformance.list.filters.toDate")}</Text>
                <Input
                  type="date"
                  value={range.toDate}
                  onChange={(e) => setRange((r) => ({ ...r, toDate: e.target.value }))}
                  h="31px"
                  w="160px"
                  fontSize="12px"
                  fontWeight="600"
                />
              </Box>

              {canSeeTeam ? (
                <Box>
                  <Text {...filterLabelProps}>{t("pilotPerformance.list.filters.roleScope")}</Text>
                  <Select
                    value={roleScope}
                    onChange={(e) => setRoleScope(e.target.value)}
                    w="180px"
                    sx={filterSelectSx}
                  >
                    <option value="all">{roleScopeLabel("all")}</option>
                    <option value="sales">{roleScopeLabel("sales")}</option>
                    <option value="recruiter">{roleScopeLabel("recruiter")}</option>
                  </Select>
                </Box>
              ) : null}

              <Button
                variant="primary"
                size="sm"
                h="31px"
                onClick={fetchData}
                isLoading={loading}
              >
                {t("pilotPerformance.list.filters.refresh")}
              </Button>
            </Flex>
          </Box>
        </Panel>

        {/* Activity charts */}
        <Panel
          title={t("pilotPerformance.list.pageTitle")}
          actions={
            <Text fontSize="11px" fontWeight="700" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
              {summaryLabel}
            </Text>
          }
        >
          {loading ? (
            <Center py="40px">
              <Spinner color="portal.primary" />
            </Center>
          ) : buckets.length === 0 ? (
            <EmptyState
              icon={<Icon as={FaChartLine} />}
              title={t("pilotPerformance.common.empty")}
              hint={t("pilotPerformance.list.emptyHint", "Adjust the period or filters to see activity metrics.")}
            />
          ) : (
            <Box p="12px">
              {showSalesSection ? (
                <>
                  <Text
                    textTransform="uppercase"
                    fontSize="10.5px"
                    fontWeight="900"
                    letterSpacing="0.055em"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.sections.sales")}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mb="16px">
                    {salesMetrics.map((metric) => (
                      <MetricCard key={metric.key} title={metric.label}>
                        {renderMetricChart(metric)}
                      </MetricCard>
                    ))}
                  </SimpleGrid>
                </>
              ) : null}

              {showRecruiterSection ? (
                <>
                  <Text
                    textTransform="uppercase"
                    fontSize="10.5px"
                    fontWeight="900"
                    letterSpacing="0.055em"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.sections.recruiter")}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" mb="16px">
                    {recruiterMetrics.map((metric) => (
                      <MetricCard key={metric.key} title={metric.label}>
                        {renderMetricChart(metric)}
                      </MetricCard>
                    ))}
                  </SimpleGrid>
                </>
              ) : null}

              {selectedBucket ? (
                <>
                  <Divider my="16px" borderColor="portal.line" />
                  <Text
                    fontSize="13px"
                    fontWeight="800"
                    color="portal.text"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("pilotPerformance.detail.chart.selectedPeriod", { period: selectedBucket.label })}
                  </Text>
                  {showSalesSection && renderMetricSummary(salesMetrics)}
                  {showRecruiterSection && (
                    <Box mt={showSalesSection ? "10px" : "0"}>{renderMetricSummary(recruiterMetrics)}</Box>
                  )}
                </>
              ) : null}
            </Box>
          )}
        </Panel>
      </Box>
    </Box>
  );
};

export default PerformanceDetail;
